import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import FocusTrap from 'focus-trap-react';
import OutsideClickHandler from 'react-outside-click-handler';
import { CartContext } from '@repo/contexts/CartContext';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import generateProductSlug from '@repo/utils/generateProductSlug';
import getPercentageDiscountPrice from '@repo/utils/getPercentageDiscountPrice';
import canApplyProStaffDiscount from '@repo/utils/canApplyProStaffDiscount';
import IconButton from '@repo/ui-components/IconButton';
import { MdClose, MdLock } from 'react-icons/md';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import sharedStyles from '../styles';
import Button from '../button';

interface CartToastProps {
  headerBackgroundColor?: string;
  headerTextColor?: string;
  textColor?: string;
}

const decodeHtml = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const Toast = styled.div<{ visible: boolean; textColor: string }>`
  position: fixed;
  top: 0;
  right: -100%;
  z-index: 99999;
  background: #f7f7f7;
  color: ${props => props.textColor};
  transition: all 0.35s ease-in-out;
  min-width: 20rem;
  max-width: 25rem;
  width: 100%;
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);

  ${({ visible }) => visible && 'right: 0'};
`;

const ToastHeader = styled.div<{
  headerBackgroundColor: string;
  headerTextColor: string;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  height: 48px;
  background: ${props => props.headerBackgroundColor};
  font-size: ${sharedStyles.fontSize.lg};
  position: relative;
  color: ${props => props.headerTextColor};
  font-weight: ${sharedStyles.fontWeight.bold};

  h2 {
    font-size: ${sharedStyles.fontSize.lg};
    color: ${props => props.headerTextColor};
    margin: 0;
  }
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 2rem;
  padding: ${sharedStyles.spacing['spacing-lg']}
    ${sharedStyles.spacing['spacing-md']};

  > img {
    margin-right: ${sharedStyles.spacing['spacing-sm']};
    width: 4rem;
    height: auto;
  }

  h3 {
    padding: 0;
    font-weight: revert;

    a {
      color: inherit;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  p {
    font-size: ${sharedStyles.fontSize.base};
    margin: 0;
    padding: 0;
  }
`;

const CheckoutArea = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ContinueShoppingLink = styled.button`
  margin-bottom: 1rem;
  text-decoration: underline;
  margin-top: 1rem;
  cursor: pointer;
  appearance: none;
  background: transparent;
  border: none;
  &:hover {
    text-decoration: none;
  }
  &:focus {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const ViewCartButton = styled.button`
  margin-bottom: 1rem;
  cursor: pointer;
  text-decoration: underline;
  border: none;
  background: transparent;

  &:hover {
    text-decoration: none;
  }
  &:focus {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  right: ${sharedStyles.spacing['spacing-1xs']};
`;

const CartToast: React.FC<CartToastProps> = ({
  headerBackgroundColor = '#000000',
  headerTextColor = '#ffffff',
  textColor = '#777777',
}) => {
  const {
    cart,
    setCartActive,
    hideToast,
    showToast,
    toastProduct,
    formatPrice,
  } = useContext(CartContext) as unknown as {
    cart: { checkoutUrl: string };
    setCartActive: (active: boolean) => void;
    hideToast: () => void;
    showToast: boolean;
    toastProduct: any;
    formatPrice: (price: number) => string;
  };
  const context = useContext(CustomerContext);
  const { tt } = useUiComponentsInternationalContext();

  // Check the site and add product sku to product url if needed
  let productSku = '';
  if (cart?.checkoutUrl?.includes('sportdog')) {
    productSku = toastProduct?.merchandise?.sku;
  }

  const tags = toastProduct?.merchandise?.product?.tags;

  const toastRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showToast) {
      toastRef.current?.focus();
    }
  }, [showToast]);

  const compareAtPrice =
    toastProduct?.merchandise?.compareAtPrice &&
    parseFloat(toastProduct.merchandise.compareAtPrice);

  const isProstaffNoSalePrice =
    (context?.isProStaff() && canApplyProStaffDiscount(tags, compareAtPrice)) ||
    false;

  const isProstaffAndSalePrice =
    (context?.isProStaff() &&
      !canApplyProStaffDiscount(tags, compareAtPrice)) ||
    false;

  const generateProductURL = () => {
    const productHandle = generateProductSlug({
      productHandle: toastProduct?.merchandise?.product?.handle,
      productType: toastProduct?.merchandise?.product?.productType,
    });

    return productSku ? `/${productHandle}/${productSku}` : `/${productHandle}`;
  };

  return (
    <Toast visible={showToast} textColor={textColor}>
      {toastProduct && (
        <OutsideClickHandler onOutsideClick={hideToast} disabled={!showToast}>
          <FocusTrap active={showToast}>
            <div>
              <ToastHeader
                headerTextColor={headerTextColor}
                headerBackgroundColor={headerBackgroundColor}
              >
                <h2>{tt(s => s.cartCommon.itemAddedToCart)}</h2>
                <CloseButtonContainer>
                  <IconButton
                    ariaLabelledBy={tt(
                      s => s.cartCommon.closeCartNotificationAriaLabelledBy
                    )}
                    title={tt(
                      s => s.cartCommon.closeCartNotificationButtonTitle
                    )}
                    onClick={hideToast}
                    icon={<MdClose size={28} fill={headerTextColor} />}
                  />
                </CloseButtonContainer>
              </ToastHeader>

              <Item>
                {toastProduct.merchandise.image?.src && (
                  <img
                    src={`${toastProduct.merchandise.image.src}&width=64`}
                    alt="" // decorative image
                  />
                )}
                <div>
                  <h3>
                    <LocalizedLink
                      aria-current={undefined}
                      to={generateProductURL()}
                    >
                      {decodeHtml(toastProduct.merchandise.product.title)}
                    </LocalizedLink>
                  </h3>

                  {isProstaffNoSalePrice && (
                    <p>
                      {tt(s => s.cartCommon.priceEach, {
                        price: formatPrice(
                          getPercentageDiscountPrice({
                            price: toastProduct.merchandise.price.amount,
                            percentageDiscount:
                              process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
                          })
                        ),
                      })}
                    </p>
                  )}

                  {/* if user is pro staff but the product is on sale */}
                  {isProstaffAndSalePrice && (
                    <p>
                      {tt(s => s.cartCommon.priceEach, {
                        price: formatPrice(
                          toastProduct.merchandise.price.amount
                        ),
                      })}
                    </p>
                  )}

                  {/* if user is not pro staff */}
                  {!context?.isProStaff() && (
                    <p>
                      {tt(s => s.cartCommon.priceEach, {
                        price: formatPrice(
                          toastProduct.merchandise.price.amount
                        ),
                      })}
                    </p>
                  )}
                </div>
              </Item>
              <CheckoutArea>
                <li>
                  <Button
                    onClick={() => {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: 'gatsby-begin-checkout',
                      });
                      localStorage.setItem('checkoutActive', 'true');
                      window.location.href = cart.checkoutUrl;
                    }}
                  >
                    <MdLock size={12} className="mr-3" />
                    {tt(s => s.cartCommon.secureCheckoutButton)}
                  </Button>
                </li>
                <li>
                  <ContinueShoppingLink
                    tabIndex={0}
                    onClick={hideToast}
                    color={textColor}
                  >
                    {tt(s => s.cartCommon.continueShoppingButton)}
                  </ContinueShoppingLink>
                </li>
                <li>
                  <ViewCartButton
                    type="button"
                    onClick={() => setCartActive(true)}
                    color={textColor}
                  >
                    {tt(s => s.cartCommon.viewCartButton)}
                  </ViewCartButton>
                </li>
              </CheckoutArea>
            </div>
          </FocusTrap>
        </OutsideClickHandler>
      )}
    </Toast>
  );
};

export default CartToast;
